import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http'
import { Injectable } from '@angular/core'
import { environment } from '@env/environment'
import { ToastController } from '@ionic/angular/standalone'
import { AuthService } from '@service/auth.service'
import { BehaviorSubject, Observable, of, throwError } from 'rxjs'
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators'

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  // Used for queued API calls while refreshing tokens
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null)
  isRefreshingToken = false

  constructor(private auth: AuthService, private toastCtrl: ToastController) {}

  // Intercept every HTTP call
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> | any {
    // full URL일 경우 바로 요청
    if (request.url.startsWith('http')) return next.handle(request)
    // Check if we need additional token logic or not
    if (this.isInBlockedList(request.url))
      return next.handle(
        request.clone({
          url: `${environment.url}${request.url}`,
        }),
      )
    return next.handle(this.addToken(request)).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse)
          switch (err.status) {
            // case 400:
            //   return this.handle400Error(err)
            case 401:
              // refresh token 요청 자체는 401 처리하지 않음 (무한 루프 방지)
              if (request.url.includes('/api/v1/refresh-token')) {
                return throwError(err)
              }
              return this.handle401Error(request, next)
            default:
              return throwError(err)
          }

        return throwError(err)
      }),
    )
  }

  // Filter out URLs where you don't want to add the token!
  private isInBlockedList(url: string): boolean {
    return [
      '/api/v1/auth/sw-version',
      '/api/v1/auth/check-username',
      '/api/v1/login',
      '/api/v1/auth-phone',
      '/api/v1/confirm-auth-phone',
      '/api/v1/register',
      '/api/v1/find-id',
      '/api/v1/update-password',
    ].includes(url)
  }

  // Add our current access token from the service if present
  private addToken(req: HttpRequest<any>) {
    return req.clone({
      ...(this.auth.currentAccessToken
        ? {
            headers: new HttpHeaders({
              authorization: `Bearer ${this.auth.currentAccessToken}`,
            }),
          }
        : {}),
      url: `${environment.url}${req.url}`,
    })
  }
  // We are not just authorized, we couldn't refresh token
  // or something else along the caching went wrong!
  private async handle400Error(err) {
    // Potentially check the exact error reason for the 400
    // then log out the user automatically
    const toast = await this.toastCtrl.create({
      message: 'Logged out due to authentication mismatch',
      duration: 2000,
    })
    toast.present()
    this.auth.logout({ redirectToHome: true })
    return of(null)
  }

  // Indicates our access token is invalid, try to load a new one
  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    console.log('JWT Interceptor: handling 401 error for', request.url)
    
    // 이미 인증되지 않은 상태이면 바로 에러 반환 (추가 요청 방지)
    if (this.auth.isAuthenticated.value === false) {
      console.log('Already logged out, skipping 401 handling')
      return throwError(new Error('User not authenticated'))
    }
    
    // Check if another call is already using the refresh logic
    if (!this.isRefreshingToken) {
      // Set to null so other requests will wait
      // until we got a new token!
      this.tokenSubject.next(null)
      this.isRefreshingToken = true
      // this.authService.currentAccessToken = null
      this.auth.setAccessToken(null)

      // First, get a new access token
      return this.auth.getNewAccessToken().pipe(
        catchError(e => {
          // Refresh token 요청 자체가 실패한 경우에만 로그아웃
          console.log('JWT Interceptor: refresh token failed, calling logout', e)
          this.auth.logout({ redirectToHome: true })
          return throwError(e)
        }),
        switchMap((token: any) => {
          if (token && token.accessToken) {
            // Store the new token
            const accessToken = token.accessToken
            return this.auth.storeAccessToken(accessToken).pipe(
              switchMap(_ => {
                // Use the subject so other calls can continue with the new token
                this.tokenSubject.next(accessToken)

                // Perform the initial request again with the new token
                // 재시도된 요청의 에러(404 등)는 여기서 처리하지 않고 호출자에게 전달
                return next.handle(this.addToken(request))
              }),
            )
          } else {
            // No new token or refresh token이 없는 경우 - 로그아웃 처리
            this.auth.logout({ redirectToHome: true })
            return throwError(new Error('No refresh token available'))
          }
        }),
        finalize(() => {
          // Unblock the token reload logic when everything is done
          this.isRefreshingToken = false
        }),
      )
    } else {
      // "Queue" other calls while we load a new token
      console.log('JWT Interceptor: token refresh in progress, queuing request for', request.url)
      return this.tokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.addToken(request))),
      )
    }
  }
}
